<template>
  <VDBaseTemplate>
    <div class="w-100 h-100" style="position: absolute; top: 70px;">
      <b-container fluid>
        <b-row>
          <b-col>
            <h2 class="tw-mb-0 text-aegis-dk">Dataset: {{ id }}</h2>
          </b-col>
          <b-col>
            <!-- <div style="float: left;">
              <img
                src="@/assets/images/beta-angle-ribbon.png"
                width=70px
                class="mt-2;"
              />
            </div> -->
            <div style="float: right;">
              <b-button 
                class="ml-1 button-link" 
                :to="{name: 'vecdyn-datasets'}" 
                variant="primary" 
                size="sm" 
                block
              >
                Return to List
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-container>
      <hr />
      <!-- MODALS -->
      <!-- https://bootstrap-vue.org/docs/components/modal -->
      <!-- This modal shows if there is a problem with the API call. -->
      <b-modal
        id="api-err-modal"
        v-model="apiErrModalShow"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Dataset Fetch Error"
        :header-bg-variant="'warning'"
        :header-text-variant="'light'">
        <span v-html="err_msg"></span>
        <b-button class="mt-3 button-link" :to="{name: 'vecdyn-datasets'}" block>Return to  dataset list</b-button>
      </b-modal>
      <!-- This modal shows when the curator clicks the "Download Table Data" button-->
      <b-modal
        id="download-csv-modal"
        v-model="downloadCSVModal"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Data Use Policy"
        :header-bg-variant="'dark'"
        :header-text-variant="'light'">
        <span v-html="'It is important for data publishers and for the reputation of data users that data is cited correctly.  Citation information for each record can be found inside the downloaded file. We would be grateful for you to also <a href=https://www.vectorbyte.org/blog/how-to-cite-us target=_blank rel=noopener noreferrer style=text-decoration:underline;>cite the VectorByte resource</a>. Data should be presumed to be licensed under <a href=https://creativecommons.org/licenses/by-nc/4.0/ target=_blank rel=noopener noreferrer style=text-decoration:underline;>CC-BY-NC</a>, but data generators may have used a more permissive license. Please check with the original data generator when in doubt.'"></span>
        <br>
        <b-button class="mt-3" style="margin-right:20px; margin-left:100px;" @click="hideModal">Cancel</b-button>
        <b-button class="mt-3" variant='primary' @click="downloadCSV">Agree and Download</b-button>
      </b-modal>  
      <!-- This modal shows when the curator clicks the "DELETE" link. -->
      <b-modal
        id="delete-dataset-modal"
        v-model="deleteDatasetModalShow"
        centered
        scrollable
        no-close-on-backdrop
        title="Confirm Dataset Deletion"
        ok-title="Yes, Delete"
        @show="resetModal"
        @hidden="resetModal"
        @ok="handleOk"
        :header-bg-variant="'danger'"
        :header-text-variant="'light'">
        <!-- <p class="my-4">{{modalText}}</p> -->
        <span v-html="'Are you sure you want to delete this dataset?'"></span>
        <form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group
          invalid-feedback="Confirmation is required"
          :state="deleteConfirmState"
        >
          <br>
          <b-form-checkbox
            id="delete-confirm-input"
            name="delete-confirm-chkbx"
            v-model="deleteConfirmed"
          >I'm sure.</b-form-checkbox>
        </b-form-group>
      </form>
      </b-modal>
      <!-- This modal shows while the dataset is being deleted. -->
      <b-modal
        id="warning-modal"
        v-model="deleteWorkingModalShow"
        centered
        scrollable
        hide-footer
        hide-header-close
        no-close-on-backdrop
        no-close-on-esc
        title="Deleting Dataset"
        :header-bg-variant="'warning'"
        :header-text-variant="'light'">
        <span v-html="'Please wait while the dataset is deleted. This may take several minutes.'"></span>
      </b-modal>
      <!-- This modal shows when the admin clicks the "Change Dataset ID" link. -->
      <b-modal
        id="change-dataset-id-modal"
        v-model="changeDatasetIdModalShow"
        centered
        scrollable
        title="Change Dataset ID"
        ok-title="Change ID"
        @show="resetChangeIdModal"
        @hidden="resetChangeIdModal"
        @ok="handleChangeIdOk"
        :header-bg-variant="'primary'"
        :header-text-variant="'light'">
        <div>
          <p><strong>Current Dataset ID:</strong> {{ id }}</p>
          <form ref="changeIdForm" @submit.stop.prevent="handleChangeIdSubmit">
            <b-form-group
              label="New Dataset ID:"
              label-for="new-dataset-id-input"
              invalid-feedback="Please enter a valid integer ID"
              :state="newDatasetIdState"
            >
              <b-form-input
                id="new-dataset-id-input"
                v-model="newDatasetId"
                type="number"
                placeholder="Enter new dataset ID"
                required
                :state="newDatasetIdState"
              ></b-form-input>
            </b-form-group>
          </form>
        </div>
      </b-modal>
      <!-- END MODALS -->
      <!-- DATASET DETAIL SECTION -->
      <b-container 
        fluid style="color: black; text-align: left; padding-left: 50px; padding-right: 50px;"
        class="data-table"
        v-if="data"
      >
        <b-row>
          <!--BEGIN LEFT COLUMN -->
          <b-col lg="7">
            <h4>Experimental Information</h4>
            <DataRow :title="'Dataset Number: '" :value="id" />
            <!-- <DataRow :title="'Species: '" :value="data[0].Species" /> -->
            <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Species</b-col>
              <b-col cols="9" class="mt-1">
                <span v-for="(item, index) in data.Species" :key="index">
                  {{ item }}<span v-if="index != data.Species.length - 1">, </span>
                </span>
              </b-col>
            </b-row>
            <!-- <DataRow :title="'Years: '" :value="data[0].Years" /> -->
            <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Years: </b-col>
              <b-col cols="9" class="mt-1">
                <span v-for="(item, index) in data.Years" :key="index">
                  {{ item }}<span v-if="index != data.Years.length - 1">, </span>
                </span>
              </b-col>
            </b-row>
            <!-- <DataRow :title="'Collection Method(s): '" :value="data[0].CollectionMethods" /> -->
            <b-row class="data-table-row">
              <b-col 
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Collection Method(s): </b-col>
              <b-col cols="9" class="mt-1">
                <span v-for="(item, index) in data.CollectionMethods" :key="index">
                  {{ item }}<span v-if="index != data.CollectionMethods.length - 1">, </span>
                </span>
              </b-col>
            </b-row>
            <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Publication Title(s): </b-col>
              <b-col cols="9" class="mt-1">
                {{ consistent_data['title'] }}
              </b-col>
            </b-row>
            <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Tag(s): </b-col>
              <!-- <b-col cols="9" class="mt-1">
                <span v-for="(item, index) in data.Tags" :key="index">
                  {{ item }}<span v-if="index != data.Tags.length - 1">, </span>
                </span>
              </b-col> -->
              <b-col cols="9" class="mt-1">
                <span v-for="(item, index) in data.Tags" :key="index">
                  <b-link 
                    :to="{
                      name: 'vecdyn-datasets',
                      query: {
                        field: 'Tags',
                        operator: 'contains',
                        term: item
                      }
                    }"
                    class="tag-link"
                  >
                    {{ item }}
                  </b-link>
                  <span v-if="index != data.Tags.length - 1">, </span>
                </span>
              </b-col>
            </b-row>
             <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Publication Description(s): </b-col>
              <b-col cols="9" class="mt-1">
                {{ consistent_data['description'] }}
              </b-col>
            </b-row>
             <b-row class="data-table-row">
              <b-col
                cols="3"
                class="mt-1 text-right border-right border-dark data-table-label-column"
              >Location(s): </b-col>
              <b-col cols="9" class="mt-1">
                {{ consistent_data['location_description'] }}
              </b-col>
            </b-row>
            <br>
            <h4>Data Source</h4>
            <b-row class="data-table-row" v-if="consistent_data['citation'] || consistent_data['figuretable']">
              <b-col cols="12" class="mt-1"> {{ consistent_data['citation'] }}<span v-if="consistent_data['figuretable']"> ( {{ consistent_data['figuretable'] }} )</span></b-col>
            </b-row>
            <DataRow :title="'Imported by: '" :value="consistent_data['submittedby']" />
            <b-row class="data-table-row">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">Dataset DOI: </b-col>
              <!-- This DOI link is going to need to be a little more robust than it is... -->
              <b-col cols="9" class="mt-1"><a v-bind:href="doiUrl(consistent_data['doi'])" target="_blank"> <u>{{ doiString(consistent_data["doi"]) }}</u></a></b-col>
            </b-row>
            <b-row class="data-table-row" v-if="relatedDatasets.length">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">More data from this source: </b-col>
              <b-col cols="9" class="mt-1">
                <span v-b-toggle.collapse-related>
                  <b-button @click="show_related_by_citation = !show_related_by_citation" variant="primary"
                  style="width: 250px; height: 40px;">
                    <span v-if="show_related_by_citation"> [-] <u>Hide related datasets</u></span>
                    <span v-else> [+] <u>See more datasets</u></span>
                  </b-button>
                </span>
                <b-collapse id="collapse-related">
                  <div class="mb-2">
                    <span>
                      See datasets related by DOI in VecDyn
                    </span>
                  </div>
                  <!-- <div class="" v-for="dataset in relatedDatasets" :key="dataset">
                    <router-link :to="{ name: 'vectraits-dataset', params: {id: dataset } }">
                      {{ dataset }}
                    </router-link>
                  </div> -->
                  <b-table
                    ref="relatedByDoiTable"
                    striped
                    hover
                    responsive
                    selectable
                    @row-selected="onRowSelected"
                    select-mode="single"
                    bordered
                    head-variant="light"
                    style="overflow-x: auto"
                    :items="relatedDatasets"
                    :fields="fieldsRelatedDatasets"
                    tbody-tr-class="vt-table-rows"
                  >
                    <!-- add tooltips for each cell -->
                    <template v-slot:cell()="data">
                      <span :title="data.value">{{ data.value }}</span>
                    </template>
                  </b-table>
                </b-collapse>
              </b-col>
            </b-row>
            <br>
            <h4>Data</h4>
            <b-row>
              <b-col>
                <div v-if="options.totalRows > -1" style="position: absolute; right: 0;  top: -25px;">
                  {{ options.totalRows }} total records in dataset
                </div>
              </b-col>
            </b-row>
            <b-row v-if="digitizedFromGraph == true">
              <b-col style="background-color: yellow;">
                Data digitized from graph
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-table
                  ref="table"
                  striped 
                  hover 
                  sticky-header="500px"
                  responsive
                  selectable
                  select-mode='single'
                  bordered
                  head-variant="light"

                  :items="timeseriesdataProvider"
                  :fields="fields"
                  :busy.sync="loading"
                  :current-page="options.currentPage"
                  :per-page="options.perPage"
                  tbody-tr-class="vt-table-row"
                >
                  <!-- The slot below formats the table headers themselves with CSS -->
                  <template #table-busy>
                    <div class="text-danger my-2">
                      <b-spinner class="align-left"></b-spinner>
                      <strong>&nbsp;Loading...</strong>
                    </div>
                  </template>
                  <!-- The slot below formats the table headers themselves with CSS -->
                  <template v-slot:head()="field">
                      <span :title="fieldDefDict[field.field.key]" style="font-size: small; white-space: nowrap;">
                          {{ field.label }}
                      </span>
                  </template>
                </b-table>
                <b-pagination
                  v-model="options.currentPage"
                  :total-rows="options.totalRows"
                  :per-page="options.perPage"
                  align="right"
                  size="sm"
                  class="my-0 mb-2"
                ></b-pagination>
                <b-button class="ml-1" style="width: 90px;" variant="primary" size="sm" @click="downloadCSVModal = true">
                  Download
                </b-button>
              </b-col>
            </b-row>
            <br>
            <h4>Additional Information</h4>
              <b-row class="data-table-row" v-for="(value, key) in additional_info" :key="key" >
                <!-- <span v-if="!['title', 'description', 'location_description'].includes(key)"> -->
                  <b-col cols="5" class="mt-1 text-right border-right border-dark data-table-label-column">
                    <span :title="fieldDefDict[key.toLowerCase()]">{{key}}: </span>
                  </b-col>
                  <b-col cols="7" class="mt-1">
                    <span v-if="key === 'publication_info_dataset_citation'" v-html="renderCitations(columnArrays[key][0])"></span>
                    <span v-else>{{ value }}</span>
                  </b-col>
                <!-- </span> -->
              </b-row>

            <!-- Subsample Data For This Dataset: only show when there are subassays or an error (not when empty). -->
            <template v-if="!subassaysLoading && (subassays.length > 0 || subassaysError)">
              <h4 class="mt-4">Subsample Data For This Dataset</h4>
              <b-card class="mb-3">
                <p v-if="subassaysError" class="text-danger mb-0">{{ subassaysError }}</p>
                <template v-else>
                  <b-table
                    :items="subassays"
                    :fields="[{ key: 'what_assayed', label: 'what_assayed' }, { key: 'assay_target', label: 'assay_target' }]"
                    striped
                    small
                    responsive
                  />
                  <b-button variant="success" size="sm" @click="downloadSubassays">
                    download subsample data
                  </b-button>
                </template>
              </b-card>
            </template>
          </b-col>
          <!--END LEFT COLUMN -->
          <!--BEGIN RIGHT COLUMN -->
          <b-col lg="5">
            <b-row>
              <b-col>
                <VecdynCollectionsByDateChart
                  :chartSeriesArray="chartSeriesArray"
                  :key="componentKey"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <VecdynSpeciesByDateChart
                  :chartSeriesArray="speciesChartArray"
                  :key="componentKey"
                />
              </b-col>
            </b-row>
            <!-- BEGIN MAP -->
            <b-row>
              <b-col v-if="mapDataAcquired"> 
              <!-- <b-col v-if="mapDataExists"> -->
                <Map 
                  :key="componentKey"
                  :mapData="mapArray"
                />
              </b-col>
            </b-row>
            <!-- END MAP -->
            <h4 v-if="admin_authorized">Admin Tools</h4>
            <b-row class="data-table-row ml-1" v-if="admin_authorized">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">Edit Publication Info: </b-col>
              <b-col cols="6" class="mt-1">
                <!-- <router-link :to="{ name: 'vecdyn-publicationinfo-edit', params: { id: this.id } }" >
                  <u>Click to edit title, DOI, embargo date, tags...</u>
                </router-link> -->
                <router-link :to="{ name: 'vecdyn-publicationinfo-edit', params: { id: this.id } }" custom v-slot="{ navigate }">
                  <span @click="navigate" role="link"><b-button class="ml-1" size="sm">Click to edit title, DOI, embargo date, tags...</b-button></span>
                </router-link>
              </b-col>
            </b-row>
            <b-row class="data-table-row ml-1" v-if="admin_authorized">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">Sub Assay File Upload: </b-col>
              <b-col cols="6" class="mt-1">
                <!-- <router-link :to="{ name: 'vecdyn-subassay-upload', params: { id: this.id } }" >
                  <u>Add a sub assay file</u>
                </router-link> -->
                <router-link :to="{ name: 'vecdyn-subassay-upload', params: { id: this.id } }" custom v-slot="{ navigate }">
                  <span @click="navigate" role="link"><b-button variant="info" class="ml-1" size="sm">Add a sub assay file</b-button></span>
                </router-link>
              </b-col>
            </b-row>
            <b-row class="data-table-row ml-1" v-if="admin_authorized">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">Delete Dataset: </b-col>
              <b-col cols="6" class="mt-1">
                <!-- <span @click="deleteDatasetModalShow = true" >
                  <u>Delete</u>
                </span> -->
                <b-button class="ml-1" style="width: 150px;" variant="danger" size="sm" @click="deleteDatasetModalShow = true">
                  Delete
                </b-button>
              </b-col>
            </b-row>
            <b-row class="data-table-row ml-1" v-if="admin_authorized">
              <b-col cols="3" class="mt-1 text-right border-right border-dark">Dataset ID: </b-col>
              <b-col cols="6" class="mt-1">
                <!-- <span @click="changeDatasetIdModalShow = true" >
                  <u>Change Dataset ID</u>
                </span> -->
                <b-button class="ml-1" style="width: 150px;" variant="warning" size="sm" @click="changeDatasetIdModalShow = true">
                  Change Dataset ID
                </b-button>
              </b-col>
            </b-row>
          </b-col>
          <!--END RIGHT COLUMN -->
        </b-row>
        <!--BEGIN BOTTOM ROW -->
        <b-row>
          <b-col>
            <div style="float: right;">
              <b-button 
                class="ml-1 mt-1 mb-3 button-link" 
                :to="{name: 'vecdyn-datasets'}" 
                variant="primary" 
                size="sm" 
                block
              >
                Return to List
              </b-button>
            </div>
          </b-col>
        </b-row>
        <!--END BOTTOM ROW -->
      </b-container>
    </div>
    
  </VDBaseTemplate>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import { mapState } from "vuex";
import * as _ from "lodash";
import HandleErrors from "@/utilities/handle-errors";
import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";
import DataRow from "@/components/details/DataRow.vue"
import VecdynCollectionsByDateChart from "@/components/charts/VecdynCollectionsByDateChart.vue";
import VecdynSpeciesByDateChart from "@/components/charts/VecdynSpeciesByDateChart.vue";
import Map from "@/components/map/VDMap.vue";

export default {
  name: "vecdyn-detail",
  mixins: [VDBaseTemplate],
  components: {
      VDBaseTemplate,
      VecdynCollectionsByDateChart,
      VecdynSpeciesByDateChart,
      Map,
      DataRow,
  },
  props: {
    id: {
      // I allowed two types because when I tried to allow only Number, and I navigated to this page from the
      // Explorer page, it worked fine. When I tried to reload the browser on this page, it would throw a type error
      // claiming that it had received a String type value for the id prop. This at least prevents the error.
      type: [Number, String],
      require: true,
    },
    embargo_key: {
      type: String,
      require: false,
    }
  },
  data(){
    return{
      options: {
        currentPage: 1,
        totalRows: 0,
        perPage: 50
      },
      mapArray: undefined,
      mapDataAcquired: false,
      loading: false,
      // admin_authorized: false,
      data: undefined, // initialize as undefined so that the v-if on the b-container will work properly.
      consistent_data: {},
      err_msg: '',
      componentKey: 0, // used to re-render map & chart components
      fields: [], // dnamically populated by buildFieldsArray()
      apiErrModalShow: false,
      downloadCSVModal: false,
      tableData: [],
      timeSeriesData: [], // the raw timeseriesdata fetched during getCollectionChartData()
      collectionChartDataByDate: {},
      chartSeriesArray: [],
      speciesChartArray: [],
      columnArrays: [], // an dictionary of arrays. Each array contains an array of each column's data.
      fieldDefDict: undefined,
      deleteDatasetModalShow: false,
      deleteWorkingModalShow: false,
      deleteConfirmed: null,
      deleteConfirmState: null,
      digitizedFromGraph: false,
      // Change Dataset ID modal properties
      changeDatasetIdModalShow: false,
      newDatasetId: '',
      newDatasetIdState: null,
      // Related datasets
      relatedDatasets: [],
      subassays: [],
      subassaysLoading: false,
      subassaysError: null,
      fieldsRelatedDatasets:[
        {
          key: "datasetid",
          tdClass: "text-truncate vt-table-cell text-underline",
        },
        {
          key: "originaltraitname",
          tdClass: "text-truncate vt-table-cell-wide",
        },
        {
          key: "interactor1",
          tdClass: "text-truncate vt-table-cell-wide",
        },
      ],
      show_related_by_citation: false,
    };
  },
  computed: {
    ...mapState("user", ["user", "myGroups"]),
    admin_authorized : function() {
      const that = this;
      // store.getters['user/inMyGroups'](['admin', 'uploader', 'collaborator'])
      let groups = ['admin', 'uploader', 'collaborator'];
      return Boolean((_.intersection(that.myGroups, groups)).length);
    },
    mapDataExists: function () {
      if (this.mapArray){
        return false;
      }
      else{
        return true;
      }
      // return false;
    },
    additional_info: function() {
      // Create a copy of this.consistent_data and remove the keys that are already displayed elsewhere on the page.
      let additional_info = Object.assign({}, this.consistent_data);
      
      delete additional_info['title'];
      delete additional_info['description'];
      delete additional_info['location_description'];
      delete additional_info['doi'];
      delete additional_info['citation'];
      return additional_info;
    },
  },
  watch: {
    id:function(){
      this.getFieldDefsandDataset();
    },
  },
  created() {
    document.title = "VecDyn Dataset: " + this.id;
    this.getFieldDefsandDataset();
    // this.getDataset(); // called in the method above
    this.getMapArray(this.id);
  },
  methods: {
    hideModal() {
      this.downloadCSVModal = false;
    },
    allArrayItemsEqual: function (arr) {
      return arr.every(val => val === arr[0]);
    },
    allArrayItemsNull: function(arr) {
      return arr.every(val => val === undefined || val === null || val === '');
    },
    forceComponentRender() {
      return this.componentKey++;
    },
    doiString: function (doi_string){
      if (doi_string != null) {
        let doi_regex = /.*(doi.org\/)/i;
        
        // /10.123456/
        if (doi_string.match(doi_regex) == -1 && 
        !(doi_string.startsWith('http') || doi_string.startsWith('https'))){
          return doi_string;
        }
        
        // http://doi.org/9999
        if (doi_string.match(doi_regex) != -1) {
          let new_string = doi_string.replace(doi_regex, '');
          if (new_string.startsWith('10.')){
            return new_string
          } else{
            return '10.' + new_string;
          }
          
        }
        // 
        return doi_string
      }else {
        return "No DOI";
      }
    },
    doiUrl: function (doi_string) {
      if (doi_string != null) {
        let doi_regex = /.*(doi.org\/)/i;
        
        // /10.123456/
        if (doi_string.match(doi_regex) == -1 && 
        !(doi_string.startsWith('http') || doi_string.startsWith('https'))){
          return 'https://www.doi.org/' + doi_string;
        }
        
        // http://doi.org/9999
        if (doi_string.match(doi_regex) != -1) {
          let new_string = doi_string.replace(doi_regex, '');
          if (new_string.startsWith('10.')){
            return 'https://www.doi.org/' + new_string;
          } else{
            return 'https://www.doi.org/10.' + new_string;
          }
        }
        //
        return doi_string
      }else {
        return "";
      }
    },
    getDataset() {
      const that = this;
      let url = `/api/vecdyn-detail/${this.id}`;

      // Check if embargo_key exists and append it as a query parameter
      if (this.embargo_key) {
        url += `?embargo_key=${this.embargo_key}`;
      }

      return HTTP.get(url)
      .then(response => {
        that.data = response.data.results;
        // related API calls
        that.getCollectionChartData();
        that.getSpeciesChartData();
        that.getRelatedByCitationRecords();
        that.fetchSubassays();

        that.forceComponentRender(); // forces re-render on map component
      })
      .catch(error => {
        that.err_msg = HandleErrors.HandleErrors(error);
        that.apiErrModalShow = true;
      });
    },
    getRelatedByCitationRecords() {
      const that = this;
      let url = `/api/vecdyn-get-related-datasets/${this.id}/`;

      // Check if embargo_key exists and append it as a query parameter
      if (this.embargo_key) {
        url += `?embargo_key=${this.embargo_key}`;
      }

      return HTTP.get(url)
      .then(response => {
        that.relatedDatasets = response.data.results;
      })
      .catch(error => {
        // If there's an error fetching related datasets, just log it and continue
        // This is not a critical failure
        HandleErrors.HandleErrors(error);
        that.relatedDatasets = [];
        return [];
      });
    },
    /** Fetch SubAssays for this dataset (for Subsample Data table). */
    fetchSubassays() {
      this.subassaysLoading = true;
      this.subassaysError = null;
      HTTP.get(`/api/vecdyn-dataset/${this.id}/subassays/`)
        .then((response) => {
          this.subassays = Array.isArray(response.data) ? response.data : [];
        })
        .catch((err) => {
          this.subassays = [];
          const d = err && err.response && err.response.data;
          this.subassaysError = (d && (d.error || d.detail)) || (err && err.message) || "Request failed";
        })
        .finally(() => {
          this.subassaysLoading = false;
        });
    },
    /** Download current SubAssays as CSV (same format as stage/commit). */
    downloadSubassays() {
      HTTP.get(`/api/vecdyn-dataset/${this.id}/subassays/download/`, { responseType: "arraybuffer" })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", `subassays_dataset_${this.id}.csv`);
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        });
    },
    downloadCSV(){
      const that = this;
      // build the params object to be sent with the request.
      var params = new URLSearchParams();
      params.append("piids", that.id);
      params.append("return_as", 'csv');

      if (this.embargo_key) {
        params.append("embargo_key", this.embargo_key);
      }

      var request = {
        params: params
      };

      this.downloadCSVModal = false;
      HTTP.get(`/api/vecdyncsv/`, request).then(response => {
        var fileURL = window.URL.createObjectURL(
          new Blob([response.data])
        );
        var fileLink = document.createElement("a");
        fileLink.href = fileURL;
        const now = new Date();
        var fileName = "vec_dyn_download_"+ that.id + "_" + now + ".csv";
        fileLink.setAttribute("download", fileName);
        document.body.appendChild(fileLink);
        fileLink.click();
      })
      .catch((error) => {
        this.err_msg = HandleErrors.HandleErrors(error);
        this.apiErrModalShow = true;
      });
    },
    getFieldDefsandDataset() {
      const that = this;

      return HTTP.get(`/api/column-definitions/`)
      .then(response => {
        let fieldDefs = response.data;

        let def_dict = {};

        fieldDefs.forEach(function(obj) {
          def_dict[obj.field_name] = obj.definition;
        })

        that.fieldDefDict = def_dict;
        that.getDataset();
      })
      .catch((error) => {
        that.err_msg = HandleErrors.HandleErrors(error);
        that.apiErrModalShow = true;
      });
    },
    timeseriesdataProvider(ctx) {
      const that = this;

      // build the params object to be sent with the request.
      var params = new URLSearchParams();
      params.append("page", ctx.currentPage)
      params.append("piids", that.id);

      if (this.embargo_key) {
        params.append("embargo_key", this.embargo_key);
      }

      var request = {
        params: params
      };

      return HTTP.get(`/api/vecdyncsv/`, request).then(response => {
        that.tableData = response.data.results;
        that.consistent_data = response.data.consistent_data;
        that.digitizedFromGraph = response.data.digitized_from_graph;
        that.options.totalRows = parseInt(response.data.count);
        that.getColumnArrays(that.tableData);
        that.fields = that.buildFieldsArray(that.tableData);
        that.loading = false;
        return that.tableData;
      })
      .catch(error => {
        this.err_msg = HandleErrors.HandleErrors(error);
        this.apiErrModalShow = true;
        return [];
      });
    },
    getMapArray: function() {
      const that = this;
      let url = `/api/vecdyn-get-map-data/${that.id}`;

      // Check if embargo_key exists and append it as a query parameter
      if (this.embargo_key) {
        url += `?embargo_key=${this.embargo_key}`;
      }

      HTTP.get(url)
      .then(response => {
        that.mapArray =  response.data;
        this.mapDataAcquired = true;
      }).catch(error => {
        that.err_msg = HandleErrors.HandleErrors(error);
        that.apiErrModalShow = true;
      });
      
    },
    getCollectionChartData(){
      const that = this;
      let url = `/api/vecdyn-detail-collection-chart/${that.id}`;

      // Check if embargo_key exists and append it as a query parameter
      if (this.embargo_key) {
        url += `?embargo_key=${this.embargo_key}`;
      }

      return HTTP.get(url)
      .then(response => {
        that.timeSeriesData = response.data;
        that.splitChartDataByDate(that.timeSeriesData);
      })
      .catch(error => {
        if (error.response == undefined){
          that.err_msg = error;
        } else {
          // this.err_msg = HandleErrors.HandleErrors(error);
          HandleErrors.HandleErrors(error)
          return [];
        }
      });
    },
    getColumnArrays(data) {
      // convert rows (horizontal data) into arrays of column data (vertical data)
      let columnArrays = [];
      let column_names = Object.getOwnPropertyNames(data[0]);

      // for each column in the dataset:
      column_names.forEach(function(column) {
        // create an array to store the current column's data
        columnArrays[column] = [];
        // for each object (row) in the dataset:
        data.forEach(function(obj) {
          // get this row's value belonging to the current column and push it into the current column's array
          columnArrays[column].push(obj[column]);
        });
      });

      this.columnArrays = columnArrays;
    },
    buildFieldsArray(fields) {
      // convert each element to an object containing the field name as a key, a sortable key, and tdclass:
      // ie. convert "Originalid" to "{ key: 'Originalid', sortable: true, tdClass: 'text-truncate vt-table-cell' },"
      // let field_list = Object.keys(data[0]);

      let field_array = [];

      fields.forEach(function(fieldName) {
          let field_dict = {};
          field_dict['key'] = fieldName;
          field_dict['sortable'] = true;
          field_dict['tdClass'] = 'text-truncate vt-table-cell';

          field_array.push(field_dict);
      });

      return field_array;
    },
    getChartDates(data){
      let dates = [];
      data.forEach(function(obj) {
        if (!dates.includes(obj['sample_end_date']))(
          dates.push(obj['sample_end_date'])
        )
      });
      return dates;
    },
    splitChartDataByDate(data) {
      const that = this;
      let dates = that.getChartDates(data)

      dates.forEach(function(date){
        let collections_on_single_date = [];
        data.forEach(function(collection){
          if (collection['sample_end_date'] == date) {
            collections_on_single_date.push(collection);
          }
        });
        that.collectionChartDataByDate[date] = collections_on_single_date;
        const date_obj = new Date(date).getTime()
        that.chartSeriesArray.push([date_obj, collections_on_single_date.length])
      });
      
      that.forceComponentRender() // forces re-render on chart component
    },
    getSpeciesChartData() {
      const that = this;
      HTTP.get(`/api/vecdyn-detail-species-by-date/${this.id}`)
      .then(response => {
        // speciesChartArray IS GOING TO HAVE TO GO INTO THE CHART COMPONENT LIKE THIS:
        // [
        //   { type: "column", name: "Culicidae Aedes wieckus", data: [ { x: JS DATE STRING, y: sample_val, name: '14915'}] },
        //   { type: "column", name: "Culicidae Anophsinger punctipennis", data: [ { x: JS DATE STRING, y: sample_val, name: '14915'}] },
        //   { type: "column", name: "Culicidae Whitfieldes grossbecki", data: [ { x: JS DATE STRING, y: sample_val, name: '14915'}] },
        // ]
        //https://stackoverflow.com/questions/70623999/highcharts-when-having-multiple-columns-for-the-same-x-axis-value-how-can-i-ge
        that.speciesChartArray = [];
        let results = response.data;
        _.forEach(results, (value, key) => {
          let series = {};
          series['type'] = 'column';
          series['name'] = key;
          series['data'] = [];
          _.forEach(results[key], (value, key) => {
            let date_obj = new Date(key).getTime();
            let data_point = {};
            data_point['x'] = date_obj;
            data_point['y'] = value;
            data_point['name'] = key;
            series['data'].push(data_point);
            
          });
          that.speciesChartArray.push(series);
        })
        
        that.forceComponentRender();
      }).catch(error => {
        that.err_msg = HandleErrors.HandleErrors(error);
        that.apiErrModalShow = true;
      });
    },
    resetModal() {
      this.deleteConfirmed = ''
      this.deleteConfirmState = null
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault()
      // Trigger submit handler
      this.handleSubmit()
    },
    handleSubmit() {
      // Exit when the form isn't valid
      if (!this.deleteConfirmed) {
        this.deleteConfirmState = false;
        return;
      }
      // Hide the modal manually
      this.deleteDatasetModalShow = false;
      this.deleteDataset();
      // this.$router.push({name: 'vectraits-exporer'});
    },
    deleteDataset() {
      this.deleteWorkingModalShow = true;
      return HTTP.post(`/api/vecdyn-dataset-delete/${this.id}/`)
      .then(response => {
        this.deleteWorkingModalShow = false;
        this.$router.push({name: 'vecdyn-datasets'})
        return response;
      })
      .catch(error => {
        this.deleteWorkingModalShow = false;
        this.err_msg = HandleErrors.HandleErrors(error);
        this.apiErrModalShow = true;
        return [];
      });
    },
    /**
     * used along with the array.filter function to reduce an array down to only the unique values.
     */
    uniqueArrayValues(value, index, self) {
      return self.indexOf(value) === index;
    },
    /**
     * Handle row selection in related datasets table
     */
    onRowSelected(items) {
      if (items && items.length > 0) {
        this.$router.push({
          name: "vecdyn-detail",
          params: { id: items[0]["datasetid"] },
          query: this.embargo_key ? { embargo_key: this.embargo_key } : {}
        });
      }
    },
    renderCitations(citation) {
      let citations = citation.split(',');
      let htmlString = ""

      citations.forEach(function (citation) {
        if (citation.startsWith("http://") ||
        citation.startsWith("https://")) {
          htmlString += (htmlString.length === 0 ? "" : ", ") + `<a href="${citation}" target="_blank">${citation}</a>`;
        }
        else if (citation.startsWith("DOI:")){
          let doi = "https://doi.org/" + citation.replace("DOI:", "");
          htmlString += (htmlString.length === 0 ? "" : ", ") + `<a href="${doi}" target="_blank">${citation}</a>`;
        } else{
          htmlString += (htmlString.length === 0 ? "" : ", ") + citation;
        }
      });

      return htmlString;
    },
    /**
     * Reset the change dataset ID modal form
     */
    resetChangeIdModal() {
      this.newDatasetId = '';
      this.newDatasetIdState = null;
    },
    /**
     * Handle the OK button click in the change dataset ID modal
     */
    handleChangeIdOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault();
      // Trigger submit handler
      this.handleChangeIdSubmit();
    },
    /**
     * Handle the form submission for changing dataset ID
     */
    handleChangeIdSubmit() {
      // Validate the input
      if (!this.newDatasetId || this.newDatasetId === '') {
        this.newDatasetIdState = false;
        return;
      }
      
      // Check if the new ID is different from the current ID
      if (parseInt(this.newDatasetId) === parseInt(this.id)) {
        this.newDatasetIdState = false;
        this.err_msg = 'New dataset ID must be different from the current ID';
        this.apiErrModalShow = true;
        return;
      }
      
      // Hide the modal manually
      this.changeDatasetIdModalShow = false;
      this.changeDatasetId();
    },
    /**
     * Make API call to change the dataset ID
     */
    changeDatasetId() {
      const that = this;
      const newId = parseInt(this.newDatasetId);
      
      return HTTP.get(`/api/vecdyn-reset-dataset-id/${this.id}/${newId}/`)
      .then(response => {
        // Show success message
        that.err_msg = `Dataset ID successfully changed from ${that.id} to ${newId}`;
        that.apiErrModalShow = true;
        
        // Update the current page ID and redirect to the new dataset page
        setTimeout(() => {
          that.$router.push({ 
            name: 'vecdyn-detail', 
            params: { id: newId },
            query: that.embargo_key ? { embargo_key: that.embargo_key } : {}
          });
        }, 2000);
        
        return response;
      })
      .catch(error => {
        that.err_msg = HandleErrors.HandleErrors(error);
        that.apiErrModalShow = true;
        return [];
      });
    }
  },
};
</script>

<style>
  h1, h2, h3, h4, a, p, text, label, table {
      color: black !important;
      text-shadow: none;
  }

  .button-link {
    color: white !important;
  }

  .data-table .data-table-row:nth-child(even){
      background-color: #CFD5E9;
  }
  .data-table .data-table-row:nth-child(odd){
      background-color: #E9EBF4;
  }
  .data-table .data-table-row{
      border-top: 2px solid white;
      min-width: 535px;
  }

  .data-table-label-column{
    
    white-space: nowrap;      /*keep text on one line */
    overflow: hidden;         /*prevent text from being shown outside the border */
    text-overflow: ellipsis;  /*cut off text with an ellipsis*/
  }

  .unplottable {
    color: red !important;
  }

  .vertical-center {
    margin: 0;
    position: absolute;
    top: 50%;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
  }

  .right-square {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }

  .both-square {
    border-radius: 0px;
  }

  .leaflet-container {
    z-index:90;
  }

  .tag-link {
    text-decoration: underline;
    color: #007bff !important; /* or your primary color */
    cursor: pointer;
  }

  .tag-link:hover {
    text-decoration: underline;
    color: #333 !important; /* darker on hover */
  }
</style>